import type { SvgIconComponent } from '@mui/icons-material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import type { CellAnnotationType } from './types';

// Capabilities a host cell exposes to the annotation-bar. The host explicitly
// names which annotation types it wants to surface via `enabled`; the bar
// shows exactly those types in the registry's declaration order.
//
// This is more direct than a flag-per-content-shape scheme because a host
// frequently needs to suppress an annotation type for reasons unrelated to
// the cell's contents (e.g. "cross-reference is enabled in principle here,
// but we haven't built the read/write hydration yet"). Listing the enabled
// types is the simplest way to express that without growing semi-arbitrary
// boolean flags every time a new constraint shows up.
export interface CellAnnotationCapabilities {
  /** Annotation types to surface in the hover bar for this cell. The bar
   *  renders one icon per type, in registry-declaration order; types
   *  outside the registry are silently dropped (so removing a type from
   *  the registry doesn't crash hosts that still list it). */
  enabled: CellAnnotationType[];
}

export interface AnnotationTypeRegistration {
  // MUI icon component (raw `@mui/icons-material/X`, not styled wrappers).
  // `SvgIconComponent` is the published type for MUI's icon set — using it
  // means the bar can pass through MUI props (fontSize, color) cleanly.
  icon: SvgIconComponent;
  /** Tooltip + accessible label shown in the picker bar. */
  label: string;
}

// Single source of truth for which annotation types exist. The bar renders
// in registry-declaration order, so keep the most-used type first.
export const ANNOTATION_REGISTRY: Record<CellAnnotationType, AnnotationTypeRegistration> = {
  cross_reference: {
    icon: AssignmentTurnedInIcon,
    label: 'Cross Reference',
  },
  pre_fx_currency: {
    icon: CurrencyExchangeIcon,
    label: 'Pre-FX Currency',
  },
};

// Resolve `capabilities.enabled` to the registry's declaration order, drop
// any type not present in the registry. Order in the bar is registry order,
// not the order the caller listed.
export const getApplicableAnnotationTypes = (
  capabilities: CellAnnotationCapabilities
): CellAnnotationType[] => {
  const enabledSet = new Set(capabilities.enabled);
  return (Object.keys(ANNOTATION_REGISTRY) as CellAnnotationType[]).filter(type => enabledSet.has(type));
};
